// Shared voice-to-text transcription boundary (PRD.279). Extracted from
// useVoiceToText.ts so both the Chief Complaint / Notes hook and the new Elia
// dictation flow call ONE transcription contract instead of copying it.
//
// This module is DOM-free and reads no `import.meta.env` / Firebase — all I/O
// deps (endpoint URL, auth token, fetch) are injected — so it is unit-testable
// under node:test. Browser-only recording helpers live in `vtt-recording.ts`.

// ─── Wire types (mirror @helios/voice-to-text-core; kept UI-side dep-free) ───

export type VttMode = 'transcription' | 'translation';

/** Field-specific summarization style. Matches SummaryStyle from voice-to-text-core. */
export type VttSummaryStyle = 'chief_complaint';

export interface VttNormalizedResult {
  text: string;
  /** PRD.271 — pre-summary raw transcript (present only when summarized). */
  rawText?: string;
  mode: VttMode;
  sourceLanguage?: string;
  targetLanguage?: string;
  provider: string;
}

// ─── Config ───

/** Maximum recording duration in seconds (shared safe cap). */
export const MAX_RECORDING_DURATION_S = 300; // 5 minutes
// NOTE: the backend `pvoiceToText` Cloud Function timeout must cover the time to
// transcribe a clip this long — see apps/voiceToText/src/index.ts (timeoutSeconds).

// ─── Transcription client ───

export interface TranscribeDeps {
  /** VITE_VOICE_TO_TEXT_URL. Absent/empty means the feature is not configured. */
  endpointUrl: string | undefined;
  /** Resolves the Firebase ID token (or null when unauthenticated). */
  getToken: () => Promise<string | null>;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export interface TranscribeArgs {
  /** Base64-encoded audio (no data-URL prefix). */
  audioBase64: string;
  mimeType: string;
  mode: VttMode;
  summaryStyle?: VttSummaryStyle;
  /** Abort the in-flight request (PRD.279 FR-4/FR-7). */
  signal?: AbortSignal;
}

/**
 * POST temporary audio to the authenticated voice-to-text backend and return the
 * normalized transcription result. Throws an `Error` with a safe message on
 * non-2xx or misconfiguration — callers map it to a friendly UI string and must
 * never surface the raw message to the user or logs.
 */
export async function transcribeAudioBlob(
  args: TranscribeArgs,
  deps: TranscribeDeps,
): Promise<VttNormalizedResult> {
  if (!deps.endpointUrl) throw new Error('Voice-to-text endpoint not configured');
  const doFetch = deps.fetchImpl ?? fetch;
  const token = await deps.getToken();

  const res = await doFetch(deps.endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      audio: args.audioBase64,
      mimeType: args.mimeType,
      mode: args.mode,
      ...(args.summaryStyle ? { summaryStyle: args.summaryStyle } : {}),
    }),
    signal: args.signal,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: res.statusText }))) as {
      error?: string;
    };
    throw new Error(body.error || `API error ${res.status}`);
  }
  return (await res.json()) as VttNormalizedResult;
}
